/**
 * AnimationPanel — Advanced Animation Studio
 *
 * Full-featured animation panel with:
 * - Entrance animations with preview
 * - Scroll-triggered animations (IntersectionObserver)
 * - Hover effect presets
 * - Easing curve selection
 * - Stagger delay for child elements
 * - Loop/repeat controls
 * - Exit animations
 * - Live animation preview per section
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  X, Sparkles, Play, Eye, ChevronDown, ChevronUp,
  Repeat, MousePointer, Scroll, Zap, Clock, Maximize2, RotateCcw
} from 'lucide-react';

// ── Animation keyframes for hover preview ────────────────────────────
const PREVIEW_KEYFRAMES_CSS = `
@keyframes previewFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes previewFadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
@keyframes previewFadeInDown { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
@keyframes previewFadeInLeft { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
@keyframes previewFadeInRight { from { opacity: 0; transform: translateX(12px); } to { opacity: 1; transform: translateX(0); } }
@keyframes previewZoomIn { from { opacity: 0; transform: scale(0.4); } to { opacity: 1; transform: scale(1); } }
@keyframes previewZoomInUp { from { opacity: 0; transform: scale(0.4) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
@keyframes previewSlideInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
@keyframes previewSlideInDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
@keyframes previewSlideInLeft { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
@keyframes previewSlideInRight { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
@keyframes previewBounceIn { 0% { opacity: 0; transform: scale(0.3); } 50% { opacity: 1; transform: scale(1.1); } 70% { transform: scale(0.9); } 100% { transform: scale(1); } }
@keyframes previewBounceInUp { 0% { opacity: 0; transform: translateY(16px); } 60% { opacity: 1; transform: translateY(-4px); } 100% { transform: translateY(0); } }
@keyframes previewFlipInX { from { opacity: 0; transform: perspective(80px) rotateX(90deg); } to { opacity: 1; transform: perspective(80px) rotateX(0); } }
@keyframes previewFlipInY { from { opacity: 0; transform: perspective(80px) rotateY(90deg); } to { opacity: 1; transform: perspective(80px) rotateY(0); } }
@keyframes previewRotateIn { from { opacity: 0; transform: rotate(-180deg) scale(0.5); } to { opacity: 1; transform: rotate(0) scale(1); } }
@keyframes previewLightSpeedIn { from { opacity: 0; transform: translateX(30px) skewX(-20deg); } to { opacity: 1; transform: translateX(0) skewX(0); } }
@keyframes previewJackInTheBox { 0% { opacity: 0; transform: scale(0.2) rotate(40deg); } 50% { transform: scale(1.1) rotate(-10deg); } 70% { transform: scale(1) rotate(3deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
@keyframes previewRollIn { from { opacity: 0; transform: translateX(-20px) rotate(-100deg); } to { opacity: 1; transform: translateX(0) rotate(0); } }
@keyframes previewTypewriter { from { opacity: 0; clip-path: inset(0 100% 0 0); } to { opacity: 1; clip-path: inset(0 0 0 0); } }
@keyframes previewBlurIn { from { opacity: 0; filter: blur(8px); } to { opacity: 1; filter: blur(0); } }
@keyframes previewClipInUp { from { clip-path: inset(100% 0 0 0); } to { clip-path: inset(0 0 0 0); } }
@keyframes previewClipInLeft { from { clip-path: inset(0 100% 0 0); } to { clip-path: inset(0 0 0 0); } }
@keyframes previewElasticIn { 0% { opacity: 0; transform: scale(0.4); } 55% { transform: scale(1.1); } 70% { transform: scale(0.95); } 85% { transform: scale(1.02); } 100% { opacity: 1; transform: scale(1); } }
@keyframes previewSwingIn { 0% { opacity: 0; transform: rotate(15deg); } 40% { transform: rotate(-10deg); } 60% { transform: rotate(5deg); } 80% { transform: rotate(-3deg); } 100% { opacity: 1; transform: rotate(0); } }
@keyframes previewDropIn { 0% { opacity: 0; transform: translateY(-20px); } 70% { transform: translateY(3px); } 100% { opacity: 1; transform: translateY(0); } }
@keyframes previewExpandIn { from { opacity: 0; transform: scaleX(0); } to { opacity: 1; transform: scaleX(1); } }
@keyframes previewGlitchIn { 0% { opacity: 0; transform: translate(4px, -2px); } 20% { opacity: 0.5; transform: translate(-3px, 2px); } 40% { opacity: 0.7; transform: translate(2px, 1px); } 60% { opacity: 0.8; transform: translate(-1px, -1px); } 80% { opacity: 0.9; transform: translate(1px, 0); } 100% { opacity: 1; transform: translate(0); } }
@keyframes previewRiseFade { from { opacity: 0; transform: translateY(10px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
@keyframes previewFadeOut { from { opacity: 1; } to { opacity: 0; } }
@keyframes previewFadeOutUp { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-12px); } }
@keyframes previewFadeOutDown { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(12px); } }
@keyframes previewZoomOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.4); } }
@keyframes previewSlideOutUp { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-20px); } }
@keyframes previewSlideOutDown { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(20px); } }
@keyframes previewBounceOut { 0% { transform: scale(1); } 25% { transform: scale(0.95); } 50% { opacity: 1; transform: scale(1.1); } 100% { opacity: 0; transform: scale(0.3); } }
`;

const PREVIEW_ANIM_MAP = {
  fadeIn: 'previewFadeIn', fadeInUp: 'previewFadeInUp', fadeInDown: 'previewFadeInDown',
  fadeInLeft: 'previewFadeInLeft', fadeInRight: 'previewFadeInRight',
  zoomIn: 'previewZoomIn', zoomInUp: 'previewZoomInUp',
  slideInUp: 'previewSlideInUp', slideInDown: 'previewSlideInDown',
  slideInLeft: 'previewSlideInLeft', slideInRight: 'previewSlideInRight',
  bounceIn: 'previewBounceIn', bounceInUp: 'previewBounceInUp',
  flipInX: 'previewFlipInX', flipInY: 'previewFlipInY',
  rotateIn: 'previewRotateIn', lightSpeedIn: 'previewLightSpeedIn',
  jackInTheBox: 'previewJackInTheBox', rollIn: 'previewRollIn',
  typewriter: 'previewTypewriter', blurIn: 'previewBlurIn',
  clipInUp: 'previewClipInUp', clipInLeft: 'previewClipInLeft',
  elasticIn: 'previewElasticIn', swingIn: 'previewSwingIn',
  dropIn: 'previewDropIn', expandIn: 'previewExpandIn',
  glitchIn: 'previewGlitchIn', riseFade: 'previewRiseFade',
  fadeOut: 'previewFadeOut', fadeOutUp: 'previewFadeOutUp', fadeOutDown: 'previewFadeOutDown',
  zoomOut: 'previewZoomOut', slideOutUp: 'previewSlideOutUp', slideOutDown: 'previewSlideOutDown',
  bounceOut: 'previewBounceOut',
};

// ── Animation Preview Dot (shown on hover over preset options) ───────
function AnimationPreviewDot({ animationValue, isHovered }) {
  if (!isHovered || !animationValue || animationValue === 'none') return null;
  const keyframeName = PREVIEW_ANIM_MAP[animationValue];
  if (!keyframeName) return null;
  return (
    <div style={{
      position: 'absolute', top: '-44px', left: '50%', transform: 'translateX(-50)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 100, pointerEvents: 'none',
    }}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '6px',
        backgroundColor: '#6366f1',
        boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
        animation: `${keyframeName} 0.8s ease-out infinite`,
      }} />
    </div>
  );
}

// ── Animation Presets ────────────────────────────────────────────────
const ENTRANCE_PRESETS = [
  { value: 'none', label: 'None' },
  { value: 'fadeIn', label: 'Fade In' },
  { value: 'fadeInUp', label: 'Fade In Up' },
  { value: 'fadeInDown', label: 'Fade In Down' },
  { value: 'fadeInLeft', label: 'Fade In Left' },
  { value: 'fadeInRight', label: 'Fade In Right' },
  { value: 'zoomIn', label: 'Zoom In' },
  { value: 'zoomInUp', label: 'Zoom In Up' },
  { value: 'slideInUp', label: 'Slide In Up' },
  { value: 'slideInDown', label: 'Slide In Down' },
  { value: 'slideInLeft', label: 'Slide In Left' },
  { value: 'slideInRight', label: 'Slide In Right' },
  { value: 'bounceIn', label: 'Bounce In' },
  { value: 'bounceInUp', label: 'Bounce In Up' },
  { value: 'flipInX', label: 'Flip Horizontal' },
  { value: 'flipInY', label: 'Flip Vertical' },
  { value: 'rotateIn', label: 'Rotate In' },
  { value: 'lightSpeedIn', label: 'Speed Fly In' },
  { value: 'jackInTheBox', label: 'Pop & Spin' },
  { value: 'rollIn', label: 'Roll In' },
  { value: 'typewriter', label: 'Typewriter', icon: '⌨️' },
  { value: 'blurIn', label: 'Blur In', icon: '🔍' },
  { value: 'clipInUp', label: 'Clip Reveal Up', icon: '📐' },
  { value: 'clipInLeft', label: 'Clip Reveal Left', icon: '📏' },
  { value: 'elasticIn', label: 'Elastic', icon: '🪀' },
  { value: 'swingIn', label: 'Swing In', icon: '🎪' },
  { value: 'dropIn', label: 'Drop In', icon: '⬇️' },
  { value: 'expandIn', label: 'Expand', icon: '⬜' },
  { value: 'glitchIn', label: 'Glitch', icon: '⚡' },
  { value: 'riseFade', label: 'Rise & Fade', icon: '🌅' },
];

const EXIT_PRESETS = [
  { value: 'none', label: 'None' },
  { value: 'fadeOut', label: 'Fade Out' },
  { value: 'fadeOutUp', label: 'Fade Out Up' },
  { value: 'fadeOutDown', label: 'Fade Out Down' },
  { value: 'zoomOut', label: 'Zoom Out' },
  { value: 'slideOutUp', label: 'Slide Out Up' },
  { value: 'slideOutDown', label: 'Slide Out Down' },
  { value: 'bounceOut', label: 'Bounce Out' },
];

const HOVER_PRESETS = [
  { value: 'none', label: 'None' },
  { value: 'grow', label: 'Grow', css: 'transform: scale(1.05)' },
  { value: 'shrink', label: 'Shrink', css: 'transform: scale(0.95)' },
  { value: 'lift', label: 'Lift Up', css: 'transform: translateY(-8px); box-shadow: 0 12px 32px rgba(0,0,0,0.15)' },
  { value: 'glow', label: 'Glow', css: 'box-shadow: 0 0 20px rgba(99,102,241,0.4)' },
  { value: 'brighten', label: 'Brighten', css: 'filter: brightness(1.1)' },
  { value: 'darken', label: 'Darken', css: 'filter: brightness(0.9)' },
  { value: 'blur-bg', label: 'Blur Background', css: 'backdrop-filter: blur(4px)' },
  { value: 'border-pop', label: 'Border Pop', css: 'border-color: var(--color-primary); box-shadow: 0 0 0 3px var(--color-primary-light)' },
  { value: 'tilt-left', label: 'Tilt Left', css: 'transform: rotate(-3deg)' },
  { value: 'tilt-right', label: 'Tilt Right', css: 'transform: rotate(3deg)' },
  { value: 'float', label: 'Float' },
  { value: 'pulse', label: 'Pulse' },
  { value: 'shake', label: 'Shake' },
  { value: 'flip3d', label: '3D Flip' },
  { value: 'neonGlow', label: 'Neon Glow' },
  { value: 'underlineSlide', label: 'Underline Slide' },
];

const EASING_PRESETS = [
  { value: 'ease', label: 'Ease' },
  { value: 'ease-in', label: 'Ease In' },
  { value: 'ease-out', label: 'Ease Out' },
  { value: 'ease-in-out', label: 'Ease In Out' },
  { value: 'linear', label: 'Linear' },
  { value: 'cubic-bezier(0.4, 0, 0.2, 1)', label: 'Smooth' },
  { value: 'cubic-bezier(0.16, 1, 0.3, 1)', label: 'Quick Start' },
  { value: 'cubic-bezier(0.68, -0.55, 0.27, 1.55)', label: 'Spring Back' },
  { value: 'cubic-bezier(0.34, 1.56, 0.64, 1)', label: 'Overshoot' },
  { value: 'cubic-bezier(0.22, 1, 0.36, 1)', label: 'Gentle Stop' },
];

const DELAY_OPTIONS = [
  { value: '0', label: 'None' },
  { value: '0.1', label: '0.1s' },
  { value: '0.2', label: '0.2s' },
  { value: '0.3', label: '0.3s' },
  { value: '0.4', label: '0.4s' },
  { value: '0.5', label: '0.5s' },
  { value: '0.6', label: '0.6s' },
  { value: '0.8', label: '0.8s' },
  { value: '1', label: '1s' },
  { value: '1.5', label: '1.5s' },
  { value: '2', label: '2s' },
];

const DURATION_OPTIONS = [
  { value: '0.3', label: 'Snappy (0.3s)' },
  { value: '0.5', label: 'Fast (0.5s)' },
  { value: '0.8', label: 'Normal (0.8s)' },
  { value: '1', label: 'Slow (1s)' },
  { value: '1.5', label: 'Very Slow (1.5s)' },
  { value: '2', label: 'Cinematic (2s)' },
];

const STAGGER_OPTIONS = [
  { value: '0', label: 'None' },
  { value: '0.05', label: '50ms' },
  { value: '0.1', label: '100ms' },
  { value: '0.15', label: '150ms' },
  { value: '0.2', label: '200ms' },
  { value: '0.3', label: '300ms' },
];

const SCROLL_THRESHOLD_OPTIONS = [
  { value: '0', label: 'Immediately visible' },
  { value: '0.1', label: '10% visible' },
  { value: '0.25', label: '25% visible' },
  { value: '0.5', label: '50% visible' },
  { value: '0.75', label: '75% visible' },
];

// ── Styles ───────────────────────────────────────────────────────────
const s = {
  panel: {
    width: 340, background: '#ffffff', borderLeft: '1px solid #e5e7eb',
    height: '100%', display: 'flex', flexDirection: 'column',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  header: {
    padding: '12px 16px', background: '#1e293b', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
  },
  headerLeft: {
    display: 'flex', alignItems: 'center', gap: 8,
  },
  closeBtn: {
    background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer',
    padding: 4, borderRadius: 4, display: 'flex', alignItems: 'center',
  },
  body: {
    flex: 1, overflowY: 'auto', padding: 0,
  },
  sectionCard: {
    borderBottom: '1px solid #f0f0f0',
  },
  sectionHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    width: '100%', padding: '10px 16px', border: 'none', background: '#ffffff',
    cursor: 'pointer', transition: 'background 0.15s',
  },
  sectionHeaderLeft: {
    display: 'flex', alignItems: 'center', gap: 8,
  },
  sectionName: {
    fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'capitalize',
  },
  sectionBadge: (active) => ({
    fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
    padding: '2px 6px', borderRadius: 4,
    background: active ? '#dcfce7' : '#f1f5f9',
    color: active ? '#16a34a' : '#94a3b8',
  }),
  controlBlock: {
    padding: '0 16px 12px',
  },
  label: {
    fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 4, fontWeight: 500,
  },
  select: {
    width: '100%', padding: '7px 10px', borderRadius: 8,
    border: '1px solid #e5e7eb', fontSize: 12, color: '#374151',
    background: '#fff', outline: 'none', cursor: 'pointer',
    transition: 'border-color 0.15s',
    fontFamily: "'Inter', sans-serif",
  },
  row: {
    display: 'flex', gap: 8, marginBottom: 8,
  },
  halfCol: {
    flex: 1,
  },
  previewBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
    width: '100%', padding: '7px 0', borderRadius: 8,
    border: '1px solid #e0e7ff', background: '#f5f3ff',
    color: '#6366f1', fontSize: 11, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
  },
  checkbox: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
    cursor: 'pointer', fontSize: 12, color: '#374151',
  },
  resetBtn: {
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '4px 8px', borderRadius: 6,
    border: '1px solid #fee2e2', background: '#fff',
    color: '#ef4444', fontSize: 10, fontWeight: 500,
    cursor: 'pointer', transition: 'all 0.15s',
  },
};

// ── Collapsible Section ──────────────────────────────────────────────
function AnimSection({ title, icon: Icon, children, defaultOpen = true, badge }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: '1px solid #f0f0f0' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{ ...s.sectionHeader, background: open ? '#f8f9fa' : '#fff' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#f8f9fa'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = open ? '#f8f9fa' : '#fff'; }}
      >
        <div style={s.sectionHeaderLeft}>
          <ChevronDown
            size={14}
            style={{ color: '#9ca3af', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}
          />
          {Icon && <Icon size={14} style={{ color: '#9ca3af' }} />}
          <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{title}</span>
          {badge && <span style={s.sectionBadge(badge !== 'none')}>{badge !== 'none' ? badge : 'Off'}</span>}
        </div>
      </button>
      {open && <div style={s.controlBlock}>{children}</div>}
    </div>
  );
}

// ── Block Card ───────────────────────────────────────────────────────
function BlockAnimationCard({ block, index, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [hoveredAnim, setHoveredAnim] = useState(null);
  const previewRef = useRef(null);
  const anim = block.animation || {
    entrance: 'none', exit: 'none', hover: 'none',
    delay: '0', duration: '0.8', easing: 'ease-out',
    scrollTrigger: false, scrollThreshold: '0.1',
    stagger: '0', loop: false, loopCount: 1,
  };

  const sectionName = (block.id || `Section ${index + 1}`).replace(/[-_]/g, ' ');
  // Support both old format (type) and new format (entrance)
  const entranceType = anim.entrance || anim.type || 'none';
  const hasAnimation = entranceType !== 'none' || (anim.hover && anim.hover !== 'none');

  const handleChange = useCallback((field, value) => {
    const newBlock = { ...block };
    if (!newBlock.animation) {
      newBlock.animation = {
        entrance: 'none', exit: 'none', hover: 'none',
        delay: '0', duration: '0.8', easing: 'ease-out',
        scrollTrigger: false, scrollThreshold: '0.1',
        stagger: '0', loop: false, loopCount: 1,
      };
    }
    newBlock.animation = { ...newBlock.animation, [field]: value };
    // Keep backward compat: sync 'type' with 'entrance'
    if (field === 'entrance') newBlock.animation.type = value;
    onUpdate(index, newBlock);
  }, [block, index, onUpdate]);

  const handleReset = useCallback(() => {
    const newBlock = { ...block };
    newBlock.animation = {
      entrance: 'none', exit: 'none', hover: 'none', type: 'none',
      delay: '0', duration: '0.8', easing: 'ease-out',
      scrollTrigger: false, scrollThreshold: '0.1',
      stagger: '0', loop: false, loopCount: 1,
    };
    onUpdate(index, newBlock);
  }, [block, index, onUpdate]);

  // Map of animation preset names to editor keyframe names
  const ANIM_MAP = {
    fadeIn: 'editorFadeIn', fadeInUp: 'editorFadeInUp', fadeInDown: 'editorFadeInDown',
    fadeInLeft: 'editorFadeInLeft', fadeInRight: 'editorFadeInRight',
    zoomIn: 'editorZoomIn', zoomInUp: 'editorZoomInUp',
    slideInUp: 'editorSlideInUp', slideInDown: 'editorSlideInDown',
    slideInLeft: 'editorSlideInLeft', slideInRight: 'editorSlideInRight',
    bounceIn: 'editorBounceIn', bounceInUp: 'editorBounceInUp',
    flipInX: 'editorFlipInX', flipInY: 'editorFlipInY',
    rotateIn: 'editorRotateIn', lightSpeedIn: 'editorLightSpeedIn',
    jackInTheBox: 'editorJackInTheBox', rollIn: 'editorRollIn',
    typewriter: 'editorTypewriter', blurIn: 'editorBlurIn',
    clipInUp: 'editorClipInUp', clipInLeft: 'editorClipInLeft',
    elasticIn: 'editorElasticIn', swingIn: 'editorSwingIn',
    dropIn: 'editorDropIn', expandIn: 'editorExpandIn',
    glitchIn: 'editorGlitchIn', riseFade: 'editorRiseFade',
  };

  // Preview animation on the section in the editor
  const handlePreview = useCallback(() => {
    // Find the section DOM element by block id or index
    const blockId = block.id;
    let el = blockId ? document.querySelector(`[data-block-id="${blockId}"]`) : null;
    if (!el) {
      // Fallback: find by section wrapper index
      const wrappers = document.querySelectorAll('[data-section-index]');
      el = wrappers[index];
    }
    if (!el) return;

    const keyframeName = ANIM_MAP[entranceType];
    if (!keyframeName) return;

    // Reset any existing animation first
    el.style.animation = 'none';
    // Force reflow to restart animation
    void el.offsetHeight;

    const duration = anim.duration || '0.8';
    const easing = anim.easing || 'ease-out';
    el.style.animation = `${keyframeName} ${duration}s ${easing} both`;

    const cleanup = () => {
      el.style.animation = '';
    };
    el.addEventListener('animationend', cleanup, { once: true });
    // Safety fallback
    setTimeout(cleanup, 4000);
  }, [block.id, index, entranceType, anim.duration, anim.easing]);

  return (
    <div style={{
      marginBottom: 8, background: expanded ? '#f8f9fa' : '#ffffff',
      borderRadius: 10, border: '1px solid', borderColor: hasAnimation ? '#c7d2fe' : '#e5e7eb',
      overflow: 'hidden', transition: 'all 0.2s',
    }}>
      {/* Card Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 12px', cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <div style={{
            width: 6, height: 6, borderRadius: '50%',
            background: hasAnimation ? '#6366f1' : '#d1d5db',
            flexShrink: 0,
          }} />
          <span style={{
            fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'capitalize',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {sectionName}
          </span>
          {hasAnimation && (
            <span style={{
              fontSize: 9, fontWeight: 600, color: '#6366f1', background: '#eef2ff',
              padding: '1px 6px', borderRadius: 4, textTransform: 'capitalize', flexShrink: 0,
            }}>
              {entranceType.replace(/([A-Z])/g, ' $1').trim()}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {hasAnimation && (
            <button
              onClick={(e) => { e.stopPropagation(); handlePreview(); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 6,
                border: '1px solid #e0e7ff', background: '#f5f3ff',
                color: '#6366f1', cursor: 'pointer', padding: 0,
              }}
              title="Preview animation"
            >
              <Play size={11} />
            </button>
          )}
          <ChevronDown
            size={14}
            style={{
              color: '#9ca3af', transition: 'transform 0.2s',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </div>
      </div>

      {/* Expanded Controls */}
      {expanded && (
        <div style={{ padding: '0 12px 12px', borderTop: '1px solid #f0f0f0' }}>

          {/* ── Entrance Animation ── */}
          <AnimSection title="Entrance" icon={Zap} badge={entranceType}>
            <div style={{ marginBottom: 8 }}>
              <label style={s.label}>Effect</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                {ENTRANCE_PRESETS.map(p => (
                  <div
                    key={p.value}
                    style={{ position: 'relative' }}
                    onMouseEnter={() => setHoveredAnim(p.value)}
                    onMouseLeave={() => setHoveredAnim(null)}
                  >
                    <AnimationPreviewDot
                      animationValue={p.value}
                      isHovered={hoveredAnim === p.value}
                    />
                    <button
                      onClick={() => handleChange('entrance', p.value)}
                      style={{
                        padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 500,
                        border: '1px solid', cursor: 'pointer', transition: 'all 0.15s',
                        borderColor: entranceType === p.value ? '#6366f1' : (hoveredAnim === p.value ? '#a5b4fc' : '#e5e7eb'),
                        background: entranceType === p.value ? '#eef2ff' : (hoveredAnim === p.value ? '#f5f3ff' : '#fff'),
                        color: entranceType === p.value ? '#4338ca' : '#374151',
                      }}
                    >
                      {p.icon || ''}{p.icon ? ' ' : ''}{p.label}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {entranceType !== 'none' && (
              <>
                <div style={s.row}>
                  <div style={s.halfCol}>
                    <label style={s.label}>Duration</label>
                    <select value={anim.duration || '0.8'} onChange={(e) => handleChange('duration', e.target.value)} style={s.select}>
                      {DURATION_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                  <div style={s.halfCol}>
                    <label style={s.label}>Delay</label>
                    <select value={anim.delay || '0'} onChange={(e) => handleChange('delay', e.target.value)} style={s.select}>
                      {DELAY_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <label style={s.label}>Motion Style</label>
                  <select value={anim.easing || 'ease-out'} onChange={(e) => handleChange('easing', e.target.value)} style={s.select}>
                    {EASING_PRESETS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </select>
                </div>

                <div style={s.row}>
                  <div style={s.halfCol}>
                    <label style={s.label}>Cascade Delay</label>
                    <select value={anim.stagger || '0'} onChange={(e) => handleChange('stagger', e.target.value)} style={s.select}>
                      {STAGGER_OPTIONS.map(st => <option key={st.value} value={st.value}>{st.label}</option>)}
                    </select>
                  </div>
                  <div style={s.halfCol}>
                    <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Repeat size={10} /> Loop
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={!!anim.loop}
                        onChange={(e) => handleChange('loop', e.target.checked)}
                        style={{ accentColor: '#6366f1', cursor: 'pointer' }}
                      />
                      {anim.loop && (
                        <input
                          type="number"
                          min="1" max="99"
                          value={anim.loopCount || 1}
                          onChange={(e) => handleChange('loopCount', parseInt(e.target.value) || 1)}
                          placeholder="∞"
                          style={{ ...s.select, width: 60, padding: '4px 6px' }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </AnimSection>

          {/* ── Scroll Trigger ── */}
          <AnimSection title="Scroll Trigger" icon={Scroll} defaultOpen={!!anim.scrollTrigger}>
            <div style={s.checkbox} onClick={() => handleChange('scrollTrigger', !anim.scrollTrigger)}>
              <input
                type="checkbox"
                checked={!!anim.scrollTrigger}
                onChange={() => {}}
                style={{ accentColor: '#6366f1', cursor: 'pointer' }}
              />
              <span style={{ fontSize: 12, color: '#374151' }}>Animate when scrolled into view</span>
            </div>
            {anim.scrollTrigger && (
              <div style={{ marginBottom: 8 }}>
                <label style={s.label}>Visibility Threshold</label>
                <select
                  value={anim.scrollThreshold || '0.1'}
                  onChange={(e) => handleChange('scrollThreshold', e.target.value)}
                  style={s.select}
                >
                  {SCROLL_THRESHOLD_OPTIONS.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 4, lineHeight: 1.4 }}>
                  Animation plays when this % of the section is visible in the viewport.
                </p>
              </div>
            )}
          </AnimSection>

          {/* ── Hover Effect ── */}
          <AnimSection title="Hover Effect" icon={MousePointer} badge={anim.hover || 'none'} defaultOpen={false}>
            <div style={{ marginBottom: 8 }}>
              <label style={s.label}>On Hover</label>
              <select
                value={anim.hover || 'none'}
                onChange={(e) => handleChange('hover', e.target.value)}
                style={s.select}
              >
                {HOVER_PRESETS.map(h => (
                  <option key={h.value} value={h.value}>{h.label}</option>
                ))}
              </select>
            </div>
            {anim.hover && anim.hover !== 'none' && (
              <p style={{ fontSize: 10, color: '#9ca3af', lineHeight: 1.4 }}>
                Visitors will see this effect when they move their cursor over this section.
              </p>
            )}
          </AnimSection>

          {/* ── Exit Animation ── */}
          <AnimSection title="Exit Animation" icon={Eye} badge={anim.exit || 'none'} defaultOpen={false}>
            <div style={{ marginBottom: 8 }}>
              <label style={s.label}>On Scroll Away</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                {EXIT_PRESETS.map(p => (
                  <div
                    key={p.value}
                    style={{ position: 'relative' }}
                    onMouseEnter={() => setHoveredAnim('exit-' + p.value)}
                    onMouseLeave={() => setHoveredAnim(null)}
                  >
                    <AnimationPreviewDot
                      animationValue={p.value}
                      isHovered={hoveredAnim === 'exit-' + p.value}
                    />
                    <button
                      onClick={() => handleChange('exit', p.value)}
                      style={{
                        padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 500,
                        border: '1px solid', cursor: 'pointer', transition: 'all 0.15s',
                        borderColor: (anim.exit || 'none') === p.value ? '#6366f1' : (hoveredAnim === 'exit-' + p.value ? '#a5b4fc' : '#e5e7eb'),
                        background: (anim.exit || 'none') === p.value ? '#eef2ff' : (hoveredAnim === 'exit-' + p.value ? '#f5f3ff' : '#fff'),
                        color: (anim.exit || 'none') === p.value ? '#4338ca' : '#374151',
                      }}
                    >
                      {p.label}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </AnimSection>

          {/* ── Preview & Reset ── */}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            {hasAnimation && (
              <button onClick={handlePreview} style={{ ...s.previewBtn, flex: 1 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#ede9fe'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#f5f3ff'; }}
              >
                <Play size={12} /> Preview
              </button>
            )}
            {hasAnimation && (
              <button onClick={handleReset} style={{ ...s.resetBtn }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
              >
                <RotateCcw size={10} /> Reset
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Panel ───────────────────────────────────────────────────────
export default function AnimationPanel({ blocks, onUpdateBlock, onClose }) {
  const animatedCount = (blocks || []).filter(b =>
    b.animation && (
      (b.animation.entrance && b.animation.entrance !== 'none') ||
      (b.animation.type && b.animation.type !== 'none') ||
      (b.animation.hover && b.animation.hover !== 'none')
    )
  ).length;

  if (!blocks || blocks.length === 0) {
    return (
      <div style={s.panel}>
        <div style={s.header}>
          <div style={s.headerLeft}>
            <Sparkles style={{ width: 16, height: 16 }} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Animations</span>
          </div>
          <button onClick={onClose} style={s.closeBtn}
            onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', textAlign: 'center' }}>
          <div>
            <Sparkles style={{ width: 40, height: 40, color: '#cbd5e1', marginBottom: 12 }} />
            <p style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>No sections to animate</p>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, lineHeight: 1.5 }}>
              Add sections to your page first, then come back to animate them.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.panel}>
      {/* Inject preview animation keyframes */}
      <style>{PREVIEW_KEYFRAMES_CSS}</style>

      {/* Header */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <Sparkles style={{ width: 16, height: 16 }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Animations</span>
          {animatedCount > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 700, background: 'rgba(99,102,241,0.2)',
              color: '#a5b4fc', padding: '1px 6px', borderRadius: 4,
            }}>
              {animatedCount} active
            </span>
          )}
        </div>
        <button onClick={onClose} style={s.closeBtn}
          onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; }}
        >
          <X style={{ width: 16, height: 16 }} />
        </button>
      </div>

      {/* Info banner */}
      <div style={{
        padding: '8px 16px', background: '#f0fdf4', borderBottom: '1px solid #dcfce7',
        fontSize: 11, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <Zap size={12} />
        Click a section to expand animation controls
      </div>

      {/* Performance warning for heavy animations */}
      {animatedCount > 5 && (
        <div style={{
          margin: '8px 12px', padding: '8px 12px', backgroundColor: '#fef3c7',
          borderRadius: '8px', fontSize: '12px', color: '#92400e',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          <span role="img" aria-label="warning">&#9889;</span> Tip: Too many animations can slow down your website. Consider using animations only on key sections.
        </div>
      )}

      {/* Section list */}
      <div style={s.body}>
        <div style={{ padding: '12px' }}>
          {blocks.map((block, idx) => (
            <BlockAnimationCard
              key={block.id || idx}
              block={block}
              index={idx}
              onUpdate={onUpdateBlock}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
